import type { ReactNode } from "react";
import { format } from "date-fns";
import { useWorkspaceCloses } from "@api/close.api";
import { useSortedCloseStages } from "@api/organizationCloseStage.api";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { fullDateFullYearFormat } from "@common/helpers/date";
import type {
    Task,
    TaskPriority as TaskPriorityValue,
} from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import { formatCloseTemplateDateRange } from "@components/settings/OrganizationCloseTemplates/formatCloseTemplateDateRange";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { BoardCardAvatarField } from "@design-system/Board/BoardCardAvatarField";
import { BoardCardTag } from "@design-system/Board/BoardCardTag";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { HideIcon } from "@icons";
import { TaskPriority } from "../TaskPrioritySelect/TaskPriority";
import { TASK_PRIORITY_CONFIG } from "../TaskPrioritySelect/taskPriority.constant";
import {
    isTaskHiddenFromClients,
    NOT_VISIBLE_TO_CLIENTS_LABEL,
    TaskVisibilityContent,
    type TaskVisibilityState,
    useTaskVisibility,
} from "../TaskView/Cells/TaskVisibility";
import { TaskCardDate, TaskCardPerson } from "./TaskBoardCardFields";
import type { OptionalCardFieldId } from "./taskCardColumns";
import { getTaskColumnName } from "./taskCardEdits";
import styles from "./TaskBoardCard.module.scss";

/** A value that doesn't name its field, so the tooltip names it. */
export const TaskCardValue = ({
    column,
    children,
}: {
    column: TaskColumn;
    children: string;
}) => (
    <BoardCardTag field={getTaskColumnName(column)} tooltip="fieldName">
        {children}
    </BoardCardTag>
);

/** The priority's icon alone, named with its value in the tooltip and for screen readers. */
export const TaskCardPriority = ({
    priority,
}: {
    priority: TaskPriorityValue;
}) => (
    <BoardCardTag
        field={getTaskColumnName(TaskColumn.PRIORITY)}
        value={TASK_PRIORITY_CONFIG[priority].label}
        tooltip="fieldAndValue"
        icon={<TaskPriority priority={priority} variant="iconOnly" />}
        className={styles.iconOnlyTag}
    />
);

const TaskCardGroup = ({ groupId }: { groupId: string }) => {
    const { data: taskGroups = [] } = useWorkspaceTaskGroups();
    const name = taskGroups.find(({ id }) => id === groupId)?.name;
    return name ? (
        <TaskCardValue column={TaskColumn.GROUP}>{name}</TaskCardValue>
    ) : null;
};

const TaskCardStage = ({ closeStageId }: { closeStageId: string }) => {
    const name = useSortedCloseStages().find(
        ({ id }) => id === closeStageId,
    )?.name;
    return name ? (
        <TaskCardValue column={TaskColumn.CLOSE_STAGE}>{name}</TaskCardValue>
    ) : null;
};

const useTaskClose = (closeId: string) => {
    const { data: closes = [] } = useWorkspaceCloses();
    return closes.find(({ id }) => id === closeId);
};

const TaskCardService = ({ closeId }: { closeId: string }) => {
    const close = useTaskClose(closeId);
    return close ? (
        <TaskCardValue column={TaskColumn.CLOSE}>{close.name}</TaskCardValue>
    ) : null;
};

const TaskCardServicePeriod = ({ closeId }: { closeId: string }) => {
    const close = useTaskClose(closeId);
    return close?.startDate || close?.endDate ? (
        <TaskCardDate field={getTaskColumnName(TaskColumn.SERVICE_PERIOD)}>
            {formatCloseTemplateDateRange(close.startDate, close.endDate)}
        </TaskCardDate>
    ) : null;
};

const VISIBILITY_FIELD = getTaskColumnName(TaskColumn.VISIBILITY);

const getVisibilityAvatarTooltip = (name: string) =>
    `${VISIBILITY_FIELD} · ${name}`;

/**
 * List's Visibility stack as 20px avatars alone, each with its own
 * `Visibility · Acme Co` tooltip, or an icon-only tag when no client sees
 * the task.
 */
export const TaskCardVisibilityContent = ({
    visibility,
}: {
    visibility: TaskVisibilityState;
}) =>
    isTaskHiddenFromClients(visibility) ? (
        <BoardCardTag
            field={VISIBILITY_FIELD}
            value={NOT_VISIBLE_TO_CLIENTS_LABEL}
            tooltip="fieldAndValue"
            icon={<HideIcon />}
            className={styles.iconOnlyTag}
        />
    ) : (
        <BoardCardAvatarField field={VISIBILITY_FIELD}>
            <TaskVisibilityContent
                visibility={visibility}
                size="sm"
                getAvatarTooltip={getVisibilityAvatarTooltip}
            />
        </BoardCardAvatarField>
    );

/** The task's visibility on its card; none while there's nothing to show. */
const TaskCardVisibility = ({ task }: { task: Task }) => {
    const visibility = useTaskVisibility(task);
    return visibility && <TaskCardVisibilityContent visibility={visibility} />;
};

const getTimestampField = (column: TaskColumn, timestamp: Date) => (
    <TaskCardDate field={getTaskColumnName(column)}>
        {format(timestamp, fullDateFullYearFormat)}
    </TaskCardDate>
);

/** Each tag field's content; one per optional card field, as typecheck enforces. */
const OPTIONAL_FIELD_CONTENT: Record<
    OptionalCardFieldId,
    (task: Task) => ReactNode
> = {
    [TaskColumn.PRIORITY]: (task) =>
        task.priority && <TaskCardPriority priority={task.priority} />,
    [TaskColumn.ESTIMATE]: (task) =>
        !!task.estimatedTime && (
            <TaskCardValue column={TaskColumn.ESTIMATE}>
                {formatDuration(task.estimatedTime)}
            </TaskCardValue>
        ),
    [TaskColumn.GROUP]: (task) =>
        task.groupId && <TaskCardGroup groupId={task.groupId} />,
    [TaskColumn.CLOSE_STAGE]: (task) =>
        task.closeStageId && <TaskCardStage closeStageId={task.closeStageId} />,
    [TaskColumn.CLOSE]: (task) =>
        task.closeId && <TaskCardService closeId={task.closeId} />,
    [TaskColumn.SERVICE_PERIOD]: (task) =>
        task.closeId && <TaskCardServicePeriod closeId={task.closeId} />,
    [TaskColumn.VISIBILITY]: (task) => <TaskCardVisibility task={task} />,
    [TaskColumn.CREATED_AT]: (task) =>
        getTimestampField(TaskColumn.CREATED_AT, task.createdAt),
    [TaskColumn.UPDATED_AT]: (task) =>
        getTimestampField(TaskColumn.UPDATED_AT, task.updatedAt),
    [TaskColumn.COMPLETED_AT]: (task) =>
        task.lastDoneAt &&
        getTimestampField(TaskColumn.COMPLETED_AT, task.lastDoneAt),
    [TaskColumn.CREATED_BY]: (task) => (
        <TaskCardPerson
            field={getTaskColumnName(TaskColumn.CREATED_BY)}
            person={task.createdBy ?? null}
        />
    ),
    [TaskColumn.COMPLETED_BY]: (task) =>
        task.lastDoneBy && (
            <TaskCardPerson
                field={getTaskColumnName(TaskColumn.COMPLETED_BY)}
                person={task.lastDoneBy}
            />
        ),
};

/** A card field beyond due date, entities and assignee, as a tag. */
export const getOptionalCardField = (
    task: Task,
    field: OptionalCardFieldId,
): BoardCardField => ({
    id: field,
    placement: "inline",
    content: OPTIONAL_FIELD_CONTENT[field](task),
});
